import { Col, Row } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { BiCheck } from "react-icons/bi";
import { HOME_WOW_DURATION, homeWowDelay } from "../../../utils/homeWow";
import { useSectionData, sectionText } from "./useSectionData";
import {
  pageBannerImage,
  pageBannerUrl,
} from "../../../Components/layouts/usePageBanner";
import PageBannerLoading from "../../../Components/layouts/PageBannerLoading";

export type LoyaltySection = {
  small_title?: string | false;
  title?: string | false;
  description?: string | false;
  subtitle?: string | false;
  button_label?: string | false;
  button_url?: string | false;
  items?: unknown;
  images?: unknown;
};

/** The mosaic has five fixed slots; each one keeps its static image as fallback. */
const staticImages = [1, 2, 3, 4, 5].map(
  (n) => `assets/imgs/cta/cta-3/img-${n}.png`
);

/**
 * Shared by the home section and the /loyalty-program page: the API content
 * with every field falling back to the static copy.
 */
export const useLoyaltyContent = () => {
  const { t } = useTranslation();
  const section = useSectionData<LoyaltySection>("loyalty-section");

  const apiItems = (Array.isArray(section?.items) ? section!.items : [])
    .map(sectionText)
    .filter((item): item is string => Boolean(item));
  const items = apiItems.length
    ? apiItems
    : ["item1", "item2", "item3"].map((key) => t(`home.loyality.${key}`));

  const lines = [section?.description, section?.subtitle]
    .map(sectionText)
    .filter((line): line is string => Boolean(line));

  const images = (Array.isArray(section?.images) ? section!.images : [])
    .map(pageBannerImage)
    .filter((src): src is string => Boolean(src));

  return {
    section,
    loading: section === undefined,
    items,
    lines,
    images,
    title: sectionText(section?.title) ?? t("home.loyality.title"),
    eyebrow: sectionText(section?.small_title) ?? t("home.loyality.btn"),
    ctaLabel: sectionText(section?.button_label) ?? t("home.loyality.discover"),
    // ponytail: anything that is not a path or a full URL falls back to the route.
    ctaUrl: pageBannerUrl(section?.button_url) ?? "/loyalty-program",
  };
};

const LoayalityProgram = () => {
  const { t } = useTranslation();
  const {
    loading,
    title,
    eyebrow,
    ctaLabel,
    ctaUrl,
    items,
    lines: apiLines,
    images: apiImages,
  } = useLoyaltyContent();

  const lines = apiLines.length
    ? apiLines
    : [
        t("home.loyality.description"),
        t("home.loyality.description1"),
        t("home.loyality.description2"),
      ];
  const images = staticImages.map((src, i) => apiImages[i] ?? src);
  const ctaClass = "btn btn-signin wow fadeInDown ac-loyalty-cta__cta";

  if (loading) return <PageBannerLoading />;

  return (
    <section className="background-body ac-home-flow-loyalty">
      <div className="box-cta-3 ac-loyalty-cta ac--font-expo mx-auto rounded-3 position-relative overflow-hidden">
        <div className="container">
          <Row className="ac-loyalty-cta__row align-items-center">
            <Col
              className="ac-loyalty-cta__col--copy"
              xxl={12}
              xl={12}
              md={12}
              lg={12}
              sm={24}
              xs={24}
            >
              <h3
                className="heading-3 mb-0 neutral-1000 wow fadeInUp ac-loyalty-cta__title ac--font-expo"
                data-wow-duration={HOME_WOW_DURATION}
                data-wow-delay={homeWowDelay(0)}
              >
                {title}
              </h3>

              <span
                className="wow fadeInDown ac-loyalty-cta__eyebrow"
                data-wow-duration={HOME_WOW_DURATION}
                data-wow-delay={homeWowDelay(1)}
              >
                {eyebrow}
              </span>

              <div
                className="ac-loyalty-cta__copy wow fadeInUp"
                data-wow-duration={HOME_WOW_DURATION}
                data-wow-delay={homeWowDelay(2)}
              >
                {lines.map((line, index) => (
                  <p key={`${line}-${index}`} className="ac-loyalty-cta__line">
                    {line}
                  </p>
                ))}
              </div>

              <ul
                className="ac-loyalty-cta__list wow fadeInUp"
                data-wow-duration={HOME_WOW_DURATION}
                data-wow-delay={homeWowDelay(3)}
              >
                {items.map((item, index) => (
                  <li key={`${item}-${index}`} className="ac-loyalty-cta__item">
                    <span className="ac-loyalty-cta__check" aria-hidden>
                      <BiCheck size={14} strokeWidth={2.5} />
                    </span>
                    <span className="ac-loyalty-cta__item-text">{item}</span>
                  </li>
                ))}
              </ul>

              {/^https?:\/\//i.test(ctaUrl) ? (
                <a
                  href={ctaUrl}
                  className={ctaClass}
                  data-wow-duration={HOME_WOW_DURATION}
                  data-wow-delay={homeWowDelay(8)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {ctaLabel}
                </a>
              ) : (
                <Link
                  to={ctaUrl}
                  className={ctaClass}
                  data-wow-duration={HOME_WOW_DURATION}
                  data-wow-delay={homeWowDelay(8)}
                >
                  {ctaLabel}
                </Link>
              )}
            </Col>
            <Col
              xxl={12}
              xl={12}
              md={12}
              lg={12}
              sm={24}
              xs={24}
              className="position-relative z-1 mt-lg-0 mt-4"
            >
              <div className="box-image-payment-2 ac-loyalty-cta__mosaic">
                <Row className="align-items-center" gutter={[16, 16]}>
                  <Col xxl={8} xl={8} lg={8} md={8} xs={8} sm={8}>
                    <div className="ac-loyalty-cta__img-wrap">
                      <img
                        className="bdrd8 w-100 wow fadeIn ac-loyalty-cta__img"
                        data-wow-duration={HOME_WOW_DURATION}
                        data-wow-delay={homeWowDelay(9)}
                        src={images[0]}
                        alt=""
                        loading="lazy"
                      />
                    </div>
                  </Col>
                  <Col xxl={8} xl={8} lg={8} md={8} xs={8} sm={8}>
                    <div className="ac-loyalty-cta__img-stack">
                      <img
                        className="bdrd8 w-100 wow fadeIn ac-loyalty-cta__img mb-15"
                        data-wow-duration={HOME_WOW_DURATION}
                        data-wow-delay={homeWowDelay(10)}
                        src={images[1]}
                        alt=""
                        loading="lazy"
                      />
                      <img
                        className="bdrd8 w-100 wow fadeIn ac-loyalty-cta__img"
                        data-wow-duration={HOME_WOW_DURATION}
                        data-wow-delay={homeWowDelay(11)}
                        src={images[2]}
                        alt=""
                        loading="lazy"
                      />
                    </div>
                  </Col>
                  <Col xxl={8} xl={8} lg={8} md={8} xs={8} sm={8}>
                    <div className="ac-loyalty-cta__img-stack">
                      <img
                        className="bdrd8 w-100 wow fadeIn ac-loyalty-cta__img mb-15"
                        data-wow-duration={HOME_WOW_DURATION}
                        data-wow-delay={homeWowDelay(12)}
                        src={images[3]}
                        alt=""
                        loading="lazy"
                      />
                      <img
                        className="bdrd8 w-100 wow fadeIn ac-loyalty-cta__img"
                        data-wow-duration={HOME_WOW_DURATION}
                        data-wow-delay={homeWowDelay(13)}
                        src={images[4]}
                        alt=""
                        loading="lazy"
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
        <div className="bg-overlay position-absolute bottom-0 end-0 h-75 background-brand-2 opacity-25 z-0 rounded-start-pill" />
      </div>
    </section>
  );
};

export default LoayalityProgram;
